import { useEffect, useRef, useState } from 'react';
import { GOOGLE_MAPS_API_SRC, GOOGLE_MAPS_SCRIPT_ID } from '../constants';
import type { ILocationData } from '../interfaces';

export interface IUseLocationPicker {
  inputRef: React.RefObject<HTMLInputElement>;
  postcode: string;
  locationInfo: { city: string; state: string; country: string };
  handleFieldChange: (field: 'city' | 'state' | 'country', value: string) => void;
  handlePostcodeChange: (value: string) => void;
}

interface IGooglePlaceComponent {
  types: string[];
  long_name: string;
}

interface IGooglePlace {
  geometry?: {
    location: { lng: () => number; lat: () => number };
  };
  address_components?: IGooglePlaceComponent[];
}

interface IGoogleAutocomplete {
  getPlace: () => IGooglePlace;
  addListener: (event: string, callback: () => void) => void;
}

declare global {
  interface Window {
    google?: {
      maps: {
        places: {
          Autocomplete: new (
            input: HTMLInputElement,
            options?: { types?: string[]; fields?: string[] },
          ) => IGoogleAutocomplete;
        };
      };
    };
  }
}

const buildLocationData = (
  info: { city: string; state: string; country: string },
  postcode: string,
  coordinates: [number, number],
): ILocationData => ({
  ...info,
  postcode,
  coordinates: { type: 'Point', coordinates },
});

export const useLocationPicker = (
  onChange: (value: string, locationData: ILocationData) => void,
): IUseLocationPicker => {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<IGoogleAutocomplete | null>(null);
  const [postcode, setPostcode] = useState<string>('');
  const [coordinates, setCoordinates] = useState<[number, number]>([0, 0]);
  const [locationInfo, setLocationInfo] = useState({ city: '', state: '', country: '' });
  const stateRef = useRef({ postcode, coordinates, locationInfo, onChange });
  stateRef.current = { postcode, coordinates, locationInfo, onChange };

  useEffect(() => {
    const emitChange = (
      info: { city: string; state: string; country: string },
      pc: string,
      coords: [number, number],
    ): void => {
      stateRef.current.onChange(
        `${info.city}, ${info.country}`,
        buildLocationData(info, pc, coords),
      );
    };

    const initAutocomplete = (): void => {
      if (!window.google?.maps || !inputRef.current) return;

      autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
        types: ['(cities)'],
        fields: ['address_components', 'geometry'],
      });

      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current?.getPlace();
        if (!place?.geometry) return;

        const components = place.address_components ?? [];
        const getComp = (type: string): string =>
          components.find((c) => c.types.includes(type))?.long_name || '';

        const city = getComp('locality') || getComp('sublocality') || getComp('postal_town');
        const state = getComp('administrative_area_level_1');
        const country = getComp('country');
        const pc = getComp('postal_code');

        const lng = place.geometry.location.lng();
        const lat = place.geometry.location.lat();

        const info = { city, state, country };
        const coords: [number, number] = [lng, lat];
        const current = stateRef.current;
        setLocationInfo(info);
        setCoordinates(coords);
        if (pc) setPostcode(pc);
        emitChange(info, pc || current.postcode, coords);
      });
    };

    const loadGoogleMaps = (): void => {
      if (window.google?.maps) {
        initAutocomplete();
        return;
      }

      if (!document.getElementById(GOOGLE_MAPS_SCRIPT_ID)) {
        const script = document.createElement('script');
        script.id = GOOGLE_MAPS_SCRIPT_ID;
        script.src = GOOGLE_MAPS_API_SRC;
        script.async = true;
        script.defer = true;
        script.onload = () => {
          if (window.google?.maps) {
            initAutocomplete();
          }
        };
        document.head.appendChild(script);
      }
    };

    loadGoogleMaps();
  }, []);

  const handleFieldChange = (field: 'city' | 'state' | 'country', value: string): void => {
    const updated = { ...locationInfo, [field]: value };
    setLocationInfo(updated);
    onChange(`${updated.city}, ${updated.country}`, buildLocationData(updated, postcode, coordinates));
  };

  const handlePostcodeChange = (value: string): void => {
    setPostcode(value);
    onChange(
      `${locationInfo.city}, ${locationInfo.country}`,
      buildLocationData(locationInfo, value, coordinates),
    );
  };

  return { inputRef, postcode, locationInfo, handleFieldChange, handlePostcodeChange };
};
